import React, { FC, FormEvent } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { Card } from 'components/Card'
import { Trans, useTranslation } from 'react-i18next'
import { TextInput } from 'components/TextInput'
import { Button } from 'components/Button'
import { Stack } from 'components/Stack'
import { Heading } from 'components/Heading'
import { Text } from 'components/Text'
import { Alert } from 'components/Alert'
import { useFormFields } from 'hooks/useFormFields'
import { Checkbox } from 'components/Checkbox'
import { Flex } from 'components/Flex'
import { useFetchInvitation, useCompleteInvitation } from '../../services/auth'
import { Loading } from 'components/Loading'
import { Logo } from 'features/shared/Logo'
import styles from './CompleteInvitation.module.scss'
import { OrganizationRepository } from 'repository/organization'

export const CompleteInvitation: FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { invitationId } = useParams()
  const { user, status: invitationStatus } = useFetchInvitation(invitationId, { retry: false })

  const [singup, { status }] = useCompleteInvitation(invitationId, user?.email || '')
  const [form, onFieldChange] = useFormFields({
    password: '',
    tos: false,
    privacy_policy: false,
  })

  if (invitationStatus === 'error') {
    navigate('/')
    return null
  }

  const onFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!form.password) return

    singup(form, {
      onSuccess: async () => {
        var organization = await OrganizationRepository.fetch()
        if (organization.subscription.level === "Freemium") {
          navigate('/explore')
        } else {
          navigate('/')
        }
      },
    })
  }

  if (!user || invitationStatus === 'loading') {
    return <Loading />
  }

  return (
    <Flex justify="center" align="center" className={styles.completeInvitation}>
      <Card>
        <Stack space="large">
          <Stack space="small">
            <Heading size="secondary">{t('complete_invitation.title')}</Heading>
            <Text>{t('complete_invitation.subtitle')}</Text>
          </Stack>

          <form onSubmit={onFormSubmit}>
            <Stack space="medium">
              <TextInput
                name="firstName"
                type="text"
                value={user.firstName}
                onChange={onFieldChange}
                label={t('complete_invitation.form.first_name')}
                readOnly
              />

              <TextInput
                name="lastName"
                type="text"
                value={user.lastName}
                onChange={onFieldChange}
                label={t('complete_invitation.form.last_name')}
                readOnly
              />

              <TextInput
                name="email"
                type="email"
                value={user.email}
                onChange={onFieldChange}
                label={t('complete_invitation.form.email')}
                readOnly
              />

              <TextInput
                name="password"
                type="password"
                value={form.password}
                onChange={onFieldChange}
                label={t('complete_invitation.form.password')}
                required
              />

              <Checkbox
                name="tos"
                label={
                  <Trans i18nKey="complete_invitation.form.tos">
                    <Link to="/terms-and-conditions">Terms and conditions</Link>
                  </Trans>
                }
                checked={form.tos}
                onChange={onFieldChange}
                className={styles.checkbox}
                required
              />

              <Checkbox
                name="privacy_policy"
                label={
                  <Trans i18nKey="complete_invitation.form.privacy">
                    <Link to="/privacy-policy">Privacy Policy</Link>.
                  </Trans>
                }
                checked={form.privacy_policy}
                onChange={onFieldChange}
                className={styles.checkbox}
                required
              />

              <Button type="submit" disabled={status === 'loading' || status === 'success'}>
                {t('complete_invitation.form.cta')}
              </Button>

              {status === 'error' && (
                <Alert tone="critical" full>
                  {t('complete_invitation.error')}
                </Alert>
              )}
            </Stack>
          </form>

          <Text>
            {t('complete_invitation.login_instead')}{' '}
            <Link to="/login">{t('complete_invitation.login_instead_cta')}</Link>
          </Text>

          <Logo />
        </Stack>
      </Card>
    </Flex>
  )
}

CompleteInvitation.displayName = 'CompleteInvitation'
